/**
 * V1 redirect shim.
 *
 * The V1 practitioner-slug booking page has been removed. This component
 * remains mounted at `/book/:slug` purely to resolve legacy links that
 * were shared with patients before the migration to V2: it looks up the
 * slug, redirects to the V2 clinic+type URL, or renders a friendly
 * "unavailable" message if no V2 route can be resolved.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle, Loader2 } from 'lucide-react';
import api from '@/services/api';

const PublicBookingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setError('Invalid booking link');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ data?: { v2_redirect_path: string | null } }>(
          `/public/book/${slug}/resolve`,
        );
        if (cancelled) return;
        const target = res.data?.v2_redirect_path;
        if (target) {
          window.location.replace(`${target}${window.location.search || ''}`);
          return;
        }
        setError(
          "This provider isn't currently accepting online bookings. Please contact the clinic.",
        );
      } catch {
        if (!cancelled) setError('This booking page is not available.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (error) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-slate-50 p-4'>
        <Card className='w-full max-w-md shadow-lg'>
          <CardContent className='pt-8 text-center'>
            <AlertCircle className='mx-auto mb-4 h-16 w-16 text-red-500' />
            <h1 className='mb-2 text-2xl font-bold text-slate-900'>Booking Unavailable</h1>
            <p className='text-slate-600'>{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='flex min-h-screen items-center justify-center bg-white'>
      <div className='text-center'>
        <Loader2 className='mx-auto mb-4 h-12 w-12 animate-spin text-teal-600' />
        <p className='text-slate-500'>Redirecting to your booking page…</p>
      </div>
    </div>
  );
};

export default PublicBookingPage;
